import { type Carer, UserRole } from '@lmc/ts-common'
import { Button, Heading, NavigationModal } from '@lmc/ui-kit'
import { Form as AntdForm, Divider, Form } from 'antd'
import { showToast } from 'common/utils'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { capitalize } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import ModalLayout from 'screens/Rostering/components/ModalLayout'
import { SHIFT, VISIT } from 'screens/Rostering/constants'
import {
  REVISION_QUERY_KEY,
  REVISIONS_QUERY_KEY
} from 'screens/Rostering/hooks/queryConstants'
import useAllocationDoubleBookings from 'screens/Rostering/hooks/useAllocationDoubleBookings'
import type { UpdateRevisionPayload } from 'screens/Rostering/hooks/useRevisionUpdate'
import DoubleBookedModal from 'screens/Rostering/RotaBuilder/screens/AllocationWindow/components/DoubleBookedModal'
import {
  AboutShiftSection,
  AboutVisitSection,
  OneOffTimeWindowSection,
  ServiceUserDetailsSection
} from 'screens/Rostering/RotaPattern/sections'
import {
  type RotaEventInstance,
  RotaInstanceStatus
} from 'screens/Rostering/types'
import {
  TIME_FORMAT,
  YEAR_FIRST_DATE_FORMAT
} from 'screens/Rostering/utils.datetime'
import { styled } from 'styled-components'
import { getRevisionFormInitialValues, getRevisionSubmitValues } from '../utils'

type EditEventModalProps = {
  editModalVisible: boolean
  onCancel: () => void
  revisionDetail: RotaEventInstance
  updateRevision?: (values: UpdateRevisionPayload) => Promise<unknown>
  isLoadingUpdate?: boolean
}

const EditEventModal = ({
  editModalVisible,
  onCancel,
  revisionDetail,
  updateRevision,
  isLoadingUpdate
}: EditEventModalProps) => {
  const [form] = AntdForm.useForm()
  const queryClient = useQueryClient()

  const userRole = useSelector(
    (state: { global: { user: Carer } }) => state.global.user?.role
  )

  const isCareHomeAdmin =
    userRole === UserRole.careHomeAdmin || userRole === UserRole.management

  const initialValues = getRevisionFormInitialValues(revisionDetail)

  const [showDoubleBookedPrompt, setShowDoubleBookedPrompt] = useState(false)
  const [doubleBook, setDoubleBook] = useState(false)
  const [pendingUpdate, setPendingUpdate] =
    useState<UpdateRevisionPayload | null>(null)

  const { doubleBookings } = useAllocationDoubleBookings()

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (editModalVisible) {
      setFormData(initialValues)
      form.setFieldsValue(initialValues)
    }
  }, [revisionDetail, editModalVisible])

  const [formData, setFormData] = useState<Record<string, unknown> | undefined>(
    initialValues
  )

  const [isDirty, setIsDirty] = useState(false)

  const [showPrompt, setShowPrompt] = useState(false)
  const [showStatusPrompt, setShowStatusPrompt] = useState(false)

  const { t } = useTranslation()

  const flags = useFeatureFlags({
    flagNames: [
      'rostering_editing_limitations',
      'rostering_admin_editing_flexibility'
    ]
  })?.data

  const flagsRosteringEditingLimitations =
    flags?.rostering_editing_limitations?.enabled

  const flagsRosteringAdminEditingFlexibility =
    flags?.rostering_admin_editing_flexibility?.enabled

  const isInProgress =
    revisionDetail?.status === RotaInstanceStatus.IN_PROGRESS ||
    revisionDetail?.clockIns?.length > 0

  const isDone = revisionDetail?.status === RotaInstanceStatus.DONE

  const isOverdue = revisionDetail?.status === RotaInstanceStatus.OVERDUE

  const canEditEventByAdmin =
    flagsRosteringAdminEditingFlexibility && isCareHomeAdmin

  const disableAddress =
    flagsRosteringEditingLimitations &&
    !canEditEventByAdmin &&
    (isInProgress || isDone)

  const disableStartTime =
    flagsRosteringEditingLimitations &&
    !canEditEventByAdmin &&
    (isInProgress || isDone || isOverdue)

  const disableEndTime =
    flagsRosteringEditingLimitations &&
    !canEditEventByAdmin &&
    (isDone || isOverdue)

  const handleCancel = () => {
    if (isDirty) {
      setShowPrompt(true)
      return
    }
    handleConfirmCancel()
  }

  const handleConfirmCancel = () => {
    onCancel()
    setFormData(undefined)
    form.setFieldsValue(initialValues)
  }

  const handleSave = async () => {
    if (isDirty && (isInProgress || isDone || isOverdue)) {
      setShowStatusPrompt(true)
      return
    }

    onSave()
  }

  const onSave = async () => {
    if (isDirty) {
      await form.validateFields()
      form.submit()
      return
    }

    if (!isLoadingUpdate) handleConfirmCancel()
  }

  const handleFormValuesChange = (values: Record<string, unknown>) => {
    if (values) {
      setIsDirty(true)
    }

    setFormData((prev) => ({ ...prev, ...values }))
  }

  const handleOnUpdateFinish = async (values: Record<string, unknown>) => {
    const startTime = form?.isFieldTouched('startTime')
    const endTime = form?.isFieldTouched('endTime')
    const isTimeTouched = startTime || endTime

    const touchedFields = form.getFieldsValue()
    const originalValues: Record<string, unknown> = initialValues
    const changedFields: Record<string, unknown> = {}

    Object.entries(touchedFields).forEach(([key, value]) => {
      if (form.isFieldTouched(key) && value !== originalValues[key]) {
        changedFields[key] = value
      }
    })

    // Invoicing override fields may not be "touched" (e.g. invoicingRateCardId is set via
    // initialValue on the FormItem, not via form.setFieldsValue), so they won't appear in
    // changedFields via the loop above. Include them whenever the user interacted with any
    // override field.
    const overrideWasTouched =
      form.isFieldTouched('overrideInvoicingRate') ||
      form.isFieldTouched('invoicingRateCardId') ||
      form.isFieldTouched('customInvoicingRate')
    if (overrideWasTouched) {
      changedFields.overrideInvoicingRate = touchedFields.overrideInvoicingRate
      changedFields.invoicingRateCardId = touchedFields.invoicingRateCardId
      changedFields.customInvoicingRate = touchedFields.customInvoicingRate
    }

    if (isDirty) {
      const updatePayload = {
        eventId: revisionDetail?.id,
        ...getRevisionSubmitValues(
          changedFields,
          isTimeTouched,
          revisionDetail?.siteShift
        )
      } as unknown as UpdateRevisionPayload

      // Check for double bookings if time was changed and there are allocated team members
      const allocatedTeamMemberIds = (revisionDetail?.teamMembers || []).map(
        (member) => member.teamMemberId
      )

      if (isTimeTouched && allocatedTeamMemberIds.length > 0) {
        // Get the base date from updatePayload.date or extract from revisionDetail.startTime
        const baseDate = updatePayload.date
          ? dayjs(updatePayload.date).format(YEAR_FIRST_DATE_FORMAT)
          : dayjs(revisionDetail?.startTime)
              .utc()
              .format(YEAR_FIRST_DATE_FORMAT)

        // Calculate startTimeForCheck
        let startTimeForCheck: string
        if (updatePayload.startTime) {
          // Combine the base date with the new start time
          startTimeForCheck = dayjs
            .utc(`${baseDate}T${updatePayload.startTime}:00.000Z`)
            .toISOString()
        } else {
          // Use the existing startTime from revisionDetail
          startTimeForCheck = revisionDetail?.startTime
        }

        // Calculate endTimeForCheck - need to handle day boundaries
        let endTimeForCheck: string
        if (updatePayload.endTime) {
          // Get the actual start time for comparison (either new or existing)
          const actualStartTime = updatePayload.startTime
            ? updatePayload.startTime
            : dayjs(revisionDetail?.startTime).utc().format(TIME_FORMAT)

          // Combine the base date with the new end time
          let endDateTime = dayjs.utc(
            `${baseDate}T${updatePayload.endTime}:00.000Z`
          )

          // If end time is earlier than or equal to start time, it's on the next day
          // Parse times to compare just the time portion (HH:mm)
          const [startHours, startMinutes] = actualStartTime
            .split(':')
            .map(Number)
          const [endHours, endMinutes] = updatePayload.endTime
            .split(':')
            .map(Number)

          const startTimeMinutes = startHours * 60 + startMinutes
          const endTimeMinutes = endHours * 60 + endMinutes

          if (endTimeMinutes <= startTimeMinutes) {
            // End time is on the next day (e.g., start 22:00, end 02:00)
            endDateTime = endDateTime.add(1, 'day')
          }

          endTimeForCheck = endDateTime.toISOString()
        } else {
          // Use the existing endTime from revisionDetail
          endTimeForCheck = revisionDetail?.endTime
        }

        let teamMembersDoubleBooked: string[]
        try {
          teamMembersDoubleBooked = await doubleBookings({
            id: revisionDetail?.id,
            teamMembers: allocatedTeamMemberIds,
            startTime: startTimeForCheck,
            endTime: endTimeForCheck
          })
        } catch {
          showToast({ title: t('SOMETHING_WENT_WRONG'), type: 'error' })
          return
        }

        // If there are double bookings and we haven't shown the prompt yet, show it
        if (!showDoubleBookedPrompt && teamMembersDoubleBooked?.length > 0) {
          updatePayload.teamMembers = {
            add: [],
            remove: [],
            doubleBooked: teamMembersDoubleBooked,
            allowDoubleBooking: false
          }
          setPendingUpdate(updatePayload)
          setShowDoubleBookedPrompt(true)
          return
        }

        // If we're here, either there are no double bookings or user confirmed
        if (teamMembersDoubleBooked?.length > 0) {
          updatePayload.teamMembers = {
            add: [],
            remove: [],
            doubleBooked: teamMembersDoubleBooked,
            allowDoubleBooking: doubleBook
          }
        }
      }

      const updateRes = await updateRevision?.(updatePayload)

      // eslint-disable-next-line no-underscore-dangle
      if ((updateRes as { _id?: string } | undefined)?._id) {
        queryClient.invalidateQueries([REVISION_QUERY_KEY, revisionDetail?.id])
        queryClient.invalidateQueries([REVISIONS_QUERY_KEY])
        setPendingUpdate(null)
        setShowDoubleBookedPrompt(false)
        setDoubleBook(false)
        handleConfirmCancel()
        setIsDirty(false)
        setFormData((prev) => ({ ...prev, ...values }))
      }
    }
  }

  const handleDoubleBookedConfirm = async (allowDoubleBooking: boolean) => {
    setDoubleBook(allowDoubleBooking)

    if (pendingUpdate) {
      if (pendingUpdate.teamMembers) {
        pendingUpdate.teamMembers.allowDoubleBooking = allowDoubleBooking
      }

      const updateRes = await updateRevision?.(pendingUpdate)

      // eslint-disable-next-line no-underscore-dangle
      if ((updateRes as { _id?: string } | undefined)?._id) {
        queryClient.invalidateQueries([REVISION_QUERY_KEY, revisionDetail?.id])
        queryClient.invalidateQueries([REVISIONS_QUERY_KEY])
        setPendingUpdate(null)
        setShowDoubleBookedPrompt(false)
        setDoubleBook(false)
        handleConfirmCancel()
        setIsDirty(false)
      }
    }
  }

  return (
    <>
      <ModalLayout
        open={editModalVisible}
        onCancel={handleCancel}
        headerHeight={8}
        headerContent={
          <>
            <StyledHeading level={2} data-cy="edit-event-modal-title">
              {t('EDIT_EVENT_DETAILS')}
            </StyledHeading>
            <StyledDivider />
          </>
        }
        footerContent={
          <FooterWrapper>
            <FooterRightSide>
              <Button onClick={handleCancel} data-cy="edit-event-cancel-btn">
                {t('CANCEL')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                isLoading={isLoadingUpdate}
                data-cy="edit-event-save-btn"
              >
                {t('SAVE')}
              </Button>
            </FooterRightSide>
          </FooterWrapper>
        }
        data-cy="edit-event-modal"
      >
        <StyledForm
          form={form}
          // @ts-ignore
          onFinish={handleOnUpdateFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={formData}
        >
          {!revisionDetail?.siteShift && (
            <ServiceUserDetailsSection
              form={form}
              formData={formData}
              disableResident
              disableAddress={disableAddress}
            />
          )}
          {revisionDetail?.siteShift ? (
            <AboutShiftSection hideTeamMembers />
          ) : (
            <AboutVisitSection hideTeamMembers />
          )}
          <OneOffTimeWindowSection
            type={revisionDetail?.siteShift ? SHIFT : VISIT}
            shortTitle
            formData={formData}
            form={form}
            disableStartTime={disableStartTime}
            disableEndTime={disableEndTime}
          />
        </StyledForm>
      </ModalLayout>
      <NavigationModal
        showPrompt={showPrompt}
        modalTitle={t('ARE_YOU_SURE')}
        modalContentText={t('EXIT_EVENT')}
        modalConfirmText={t('EXIT')}
        modalCancelText={t('CANCEL')}
        confirmNavigation={() => {
          setShowPrompt(false)
          setIsDirty(false)
          handleConfirmCancel()
        }}
        cancelNavigation={() => setShowPrompt(false)}
      />
      {flagsRosteringAdminEditingFlexibility && (
        <NavigationModal
          showPrompt={showStatusPrompt}
          modalTitle={t('ARE_YOU_SURE_CONTINUE')}
          modalContentText={t('EDIT_EVENT_IN_STATUS', {
            eventStatus: capitalize(revisionDetail?.status)
          })}
          modalConfirmText={t('CONTINUE')}
          modalCancelText={t('CANCEL')}
          confirmNavigation={() => {
            setShowStatusPrompt(false)
            onSave()
          }}
          cancelNavigation={() => setShowStatusPrompt(false)}
          confirmButtonProps={{ variant: 'primary' }}
        />
      )}
      <DoubleBookedModal
        showPrompt={showDoubleBookedPrompt}
        setShowPrompt={setShowDoubleBookedPrompt}
        confirmNavigation={(allowDoubleBooking) =>
          handleDoubleBookedConfirm(allowDoubleBooking ?? false)
        }
        isDoubleBookedSelected={doubleBook}
        setIsDoubleBookedSelected={setDoubleBook}
        cancelNavigation={() => {
          setShowDoubleBookedPrompt(false)
          setPendingUpdate(null)
          setDoubleBook(false)
          // Reset form to initial values
          form.setFieldsValue(initialValues)
          setFormData(initialValues)
          setIsDirty(false)
        }}
      />
    </>
  )
}

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StyledHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  font-size: 24px;
`
const StyledDivider = styled(Divider)`
  margin: 16px 0;
`
const FooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`
const FooterRightSide = styled.div`
  display: flex;
  gap: 8px;
`

export default EditEventModal
